// src/BlitzHowTo/TutorialDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Fade,
  Slide,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  CheckCircle as CompleteIcon,
  Explore as ExploreIcon,
  Navigation as NavigationIcon,
  EmojiEvents as TrophyIcon,
  Payment as PaymentIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  Handshake as HandshakeIcon,
  Info as InfoIcon,
  Star as StarIcon,
  Email as EmailIcon,
  Payments as PaymentsIcon,
  AccountCircle as AccountCircleIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  CheckCircle as CheckCircleIcon,
  Group as GroupIcon,
  People as PeopleIcon,
  Dashboard as DashboardIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { styled, alpha, useTheme, keyframes } from '@mui/material/styles';

// Icon mapping - Enhanced with all icons used in tutorials
export const iconMap = {
  explore: ExploreIcon,
  navigation: NavigationIcon,
  trophy: TrophyIcon,
  payment: PaymentIcon,
  account_balance: BankIcon,
  receipt: ReceiptIcon,
  handshake: HandshakeIcon,
  info: InfoIcon,
  star: StarIcon,
  email: EmailIcon,
  payments: PaymentsIcon,
  // Navbar & Profile icons
  account_circle: AccountCircleIcon,
  notifications: NotificationsIcon,
  settings: SettingsIcon,
  menu: MenuIcon,
  check_circle: CheckCircleIcon,
  groups: GroupIcon,
  // Additional icons for menu items
  people: PeopleIcon,
  dashboard: DashboardIcon,
  send: SendIcon,
};

// Animations
const slideIn = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(3),
    background: theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`
      : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    boxShadow: theme.palette.mode === 'dark'
      ? `0 20px 60px ${alpha(theme.palette.common.black, 0.5)}`
      : `0 20px 60px ${alpha(theme.palette.common.black, 0.15)}`,
    maxWidth: '600px',
    width: '90vw',
  },
}));

const TutorialContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(4),
  textAlign: 'center',
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 80,
  height: 80,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(theme.palette.secondary.main, 0.2)})`,
  border: `2px solid ${theme.palette.primary.main}`,
  marginBottom: theme.spacing(3),
  animation: `${pulse} 2s infinite ease-in-out`,
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 3,
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  '& .MuiLinearProgress-bar': {
    borderRadius: 3,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

const StepContent = styled(Box)(({ theme }) => ({
  animation: `${slideIn} 0.3s ease-out`,
  minHeight: 200,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const BlitzHowTo = ({
  open,
  onClose,
  tutorial,
  onComplete,
  onSkip,
}) => {
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (open) {
      setCurrentStep(0);
    }
  }, [open]);

  if (!tutorial || !tutorial.steps || tutorial.steps.length === 0) {
    return null;
  }

  const { steps, title } = tutorial;
  const totalSteps = steps.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;
  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;

  const currentStepData = steps[currentStep];
  const IconComponent = iconMap[currentStepData.icon] || ExploreIcon;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete();
    }
    onClose();
  };

  const handleSkipAll = () => {
    if (onSkip) {
      onSkip();
    }
    onClose();
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleSkipAll}
      maxWidth="md"
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 400 }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Close/Skip Button */}
        <IconButton
          onClick={handleSkipAll}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            background: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(10px)',
            '&:hover': {
              background: alpha(theme.palette.background.paper, 0.9),
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <TutorialContainer>
          {/* Tutorial Title */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {title}
          </Typography>

          {/* Progress Bar */}
          <Box sx={{ mb: 4, px: 4 }}>
            <ProgressBar variant="determinate" value={progress} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: 'block' }}
            >
              Step {currentStep + 1} of {totalSteps}
            </Typography>
          </Box>

          {/* Step Content */}
          <StepContent key={currentStep}>
            <IconContainer>
              <IconComponent
                sx={{
                  fontSize: 40,
                  color: theme.palette.primary.main,
                }}
              />
            </IconContainer>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: theme.palette.text.primary,
              }}
            >
              {currentStepData.title}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: '80%',
                mx: 'auto',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              {currentStepData.content}
            </Typography>
          </StepContent>

          {/* Step Indicators */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              mb: 3,
            }}
          >
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: index === currentStep
                    ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                    : alpha(theme.palette.text.secondary, 0.2),
                  transition: 'all 0.3s ease',
                  transform: index === currentStep ? 'scale(1.2)' : 'scale(1)',
                }}
              />
            ))}
          </Box>

          {/* Navigation Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              mt: 3,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleBack}
              disabled={isFirstStep}
              sx={{
                borderRadius: 25,
                px: 3,
                textTransform: 'none',
                fontWeight: 600,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                },
              }}
            >
              Back
            </Button>

            <Button
              variant="text"
              onClick={handleSkipAll}
              sx={{
                borderRadius: 25,
                px: 3,
                textTransform: 'none',
                color: theme.palette.text.secondary,
              }}
            >
              Skip Tutorial
            </Button>

            <Button
              variant="contained"
              endIcon={isLastStep ? <CompleteIcon /> : <NextIcon />}
              onClick={handleNext}
              sx={{
                borderRadius: 25,
                px: 4,
                textTransform: 'none',
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                  boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
              }}
            >
              {isLastStep ? 'Complete' : 'Next'}
            </Button>
          </Box>
        </TutorialContainer>
      </DialogContent>
    </StyledDialog>
  );
};

export default BlitzHowTo;